import { useEffect, useState } from "react";
import { useS3FileUploadDropper } from "pages/geoscape-admin/custom-datasets/custom-hook/customHooks";
import { useIdentity } from "contexts/identity-context";
import { resolveIdentityId } from "utils/identity";

export type UploadState = "ready" | "uploading" | "uploaded";

// Must match the backend's MAX_UPLOAD_ZIP_BYTES; a larger zip runs the conversion Lambda out of memory.
export const MAX_UPLOAD_ZIP_BYTES = 50 * 1024 * 1024;
export const MAX_UPLOAD_ZIP_MB = MAX_UPLOAD_ZIP_BYTES / (1024 * 1024);

// Must match the backend's settings.clip_bucket.
export const CLIP_UPLOAD_BUCKET =
  import.meta.env.VITE_ENVIRONMENT === "production"
    ? "prod-hub-clip-storage"
    : "preprod-hub-clip-storage";

interface UseGeometryUploadOptions {
  // Called with the zip's S3 key once it is uploaded; the backend converts it in a background job.
  onZipUploaded: (s3Key: string) => void;
}

export function useGeometryUpload({ onZipUploaded }: UseGeometryUploadOptions) {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);

  const [uploadState, setUploadState] = useState<UploadState>("ready");
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);

  const {
    uploadProgress,
    isUploading,
    haveUploaded,
    files,
    removeFile,
    getRootProps,
    getInputProps,
    uploadFiles,
    s3UploadFailed,
  } = useS3FileUploadDropper({
    maxSize: MAX_UPLOAD_ZIP_BYTES,
    onDropRejected: (rejections) => {
      const tooLarge = rejections.some((r) =>
        r.errors.some((e) => e.code === "file-too-large"),
      );
      setRejectionMessage(
        tooLarge
          ? `The file is too large. The maximum is ${MAX_UPLOAD_ZIP_MB} MB.`
          : rejections[0].errors[0].message,
      );
    },
    maxFiles: 1,
    multiple: false,
    prefixFileName: `${identityId}/raw_input/`,
    acceptExtension: {
      "application/zip": [".zip"],
      "application/x-zip-compressed": [".zip"],
    },
  });

  useEffect(() => {
    if (files.length > 0 && !isUploading) {
      setRejectionMessage(null);
      uploadFiles(CLIP_UPLOAD_BUCKET);
      setUploadState("uploading");
    }
  }, [files]);

  useEffect(() => {
    if (haveUploaded === true) {
      onZipUploaded(`${identityId}/raw_input/${files[0].name}`);
      setUploadState("uploaded");
      files.forEach((x) => removeFile(x.name));
    }
  }, [haveUploaded]);

  useEffect(() => {
    if (s3UploadFailed && uploadState === "uploading") {
      setRejectionMessage("The file couldn't be uploaded. Try again.");
      setUploadState("ready");
      files.forEach((x) => removeFile(x.name));
    }
  }, [s3UploadFailed]);

  const cancelUpload = (path: string) => {
    removeFile(path);
    setUploadState("ready");
  };

  return {
    uploadState,
    rejectionMessage,
    uploadProgress,
    isUploading,
    haveUploaded,
    files,
    getRootProps,
    getInputProps,
    cancelUpload,
  };
}
